/**
 * The period picker every reporting screen shares (#169).
 *
 * Sales had its own copy of this and menu engineering had none, so "last
 * week" was one screen's idea of a week and nowhere else's. The periods and
 * the nights they resolve to are `@baros/core`'s `period.ts`; what lives
 * here is the row of buttons and the two date boxes.
 */

import { PERIOD_CHOICES, isDateString, type DateWindow, type Period } from "@baros/core";

export function PeriodPicker({
  period,
  shown,
  disabled,
  onChange,
  label = "Which nights to show",
}: {
  readonly period: Period;
  /** The nights the current period resolves to, for seeding a range. */
  readonly shown: DateWindow;
  readonly disabled?: boolean;
  readonly onChange: (period: Period) => void;
  readonly label?: string;
}) {
  return (
    <div className="row period-pick" role="group" aria-label={label}>
      {PERIOD_CHOICES.map((c) => (
        <button
          key={c.kind}
          type="button"
          className="btn"
          aria-pressed={period.kind === c.kind}
          disabled={disabled}
          onClick={() => {
            if (c.kind === "range") {
              // Opened on the nights already showing, so the boxes start
              // somewhere useful rather than on today twice.
              if (period.kind !== "range") onChange({ kind: "range", from: shown.from, to: shown.to });
              return;
            }
            onChange({ kind: c.kind });
          }}
        >
          {c.label}
        </button>
      ))}
      {period.kind === "range" && (
        <span className="period-range">
          <input
            type="date"
            aria-label="From"
            value={period.from}
            max={period.to}
            disabled={disabled}
            onChange={(e) => isDateString(e.target.value) && onChange({ ...period, from: e.target.value })}
          />
          <span className="label">to</span>
          <input
            type="date"
            aria-label="To"
            value={period.to}
            min={period.from}
            disabled={disabled}
            onChange={(e) => isDateString(e.target.value) && onChange({ ...period, to: e.target.value })}
          />
        </span>
      )}
    </div>
  );
}
